import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Accordion, AccordionSummary, AccordionDetails,
  CircularProgress, Pagination, FormControlLabel, Checkbox, FormGroup,
  Select, MenuItem, InputLabel, FormControl, Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  RecordVoiceOver as RecordVoiceOverIcon
} from '@mui/icons-material';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const TranscriptionsHistory = () => {
  const [transcriptions, setTranscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTranscription, setSelectedTranscription] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [exporting, setExporting] = useState(null);
  const [exportOptions, setExportOptions] = useState({
    includeTranscription: true,
    includeTimestamps: true,
    includeSummary: true,
    includeSpeakers: true
  });
  const [diarizationTarget, setDiarizationTarget] = useState(null);
  const [diarizationNumSpeakers, setDiarizationNumSpeakers] = useState('auto');
  const [diarizationJobId, setDiarizationJobId] = useState(null);
  const [diarizationStatus, setDiarizationStatus] = useState('idle');
  const [diarizationMessage, setDiarizationMessage] = useState('');
  const [diarizationError, setDiarizationError] = useState('');

  const itemsPerPage = 10;

  useEffect(() => {
    fetchTranscriptions();
  }, [page, searchTerm]);

  const fetchTranscriptions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/transcriptions', {
        params: {
          limit: itemsPerPage,
          offset: (page - 1) * itemsPerPage,
          search: searchTerm
        }
      });

      setTranscriptions(response.data.items);
      setTotalPages(Math.ceil(response.data.total / itemsPerPage));
      return response.data.items;
    } catch (error) {
      console.error('Error fetching transcriptions:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (transcriptionId, format) => {
    try {
      setExporting(format);
      const params = {
        include_transcription: exportOptions.includeTranscription,
        include_timestamps: exportOptions.includeTimestamps,
        include_summary: exportOptions.includeSummary,
        include_speakers: exportOptions.includeSpeakers
      };

      const response = await axios.get(`/api/export/${transcriptionId}/${format}`, {
        params: params,
        responseType: 'blob'
      });

      // Get filename from response headers or use a default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `transcription_${transcriptionId}.${format}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
      alert('Error al exportar el archivo');
    } finally {
      setExporting(null);
    }
  };

  const handleDelete = async (transcriptionId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta transcripción?')) {
      return;
    }

    try {
      await axios.delete(`/api/transcription/${transcriptionId}`);
      fetchTranscriptions(); // Refresh list
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Error al eliminar la transcripción');
    }
  };


  const openDiarization = (transcription) => {
    setDiarizationTarget(transcription);
    setDiarizationNumSpeakers(transcription.num_speakers ? String(transcription.num_speakers) : 'auto');
    setDiarizationStatus('idle');
    setDiarizationMessage('');
    setDiarizationError('');
  };

  const closeDiarization = () => {
    setDiarizationTarget(null);
    setDiarizationStatus('idle');
    setDiarizationMessage('');
    setDiarizationError('');
  };

  const startDiarization = async () => {
    if (!diarizationTarget) return;

    setDiarizationStatus('processing');
    setDiarizationError('');
    setDiarizationMessage('Iniciando separación de hablantes...');

    try {
      const response = await axios.post(
        `/api/transcription/${diarizationTarget.id}/diarize`,
        {
          num_speakers: diarizationNumSpeakers === 'auto' ? null : Number(diarizationNumSpeakers)
        }
      );
      setDiarizationJobId(response.data.job_id);
    } catch (error) {
      setDiarizationStatus('failed');
      setDiarizationError(error.response?.data?.detail || 'No se pudo iniciar la diarización');
    }
  };

  useEffect(() => {
    if (!diarizationJobId) return undefined;

    let active = true;
    const poll = async () => {
      try {
        const response = await axios.get(`/api/diarization/job/${diarizationJobId}`);
        if (!active) return;

        const job = response.data;
        setDiarizationMessage(job.message || '');

        if (job.status === 'completed') {
          setDiarizationStatus('completed');
          setDiarizationJobId(null);
          const items = await fetchTranscriptions();
          if (diarizationTarget) {
            const updated = items.find((item) => item.id === diarizationTarget.id);
            if (updated) setDiarizationTarget(updated);
          }
        } else if (job.status === 'failed') {
          setDiarizationStatus('failed');
          setDiarizationError(job.error || 'No se pudieron separar los hablantes');
          setDiarizationJobId(null);
          await fetchTranscriptions();
        }
      } catch (error) {
        console.error('Error polling diarization job:', error);
      }
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [diarizationJobId]);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        📚 Historial de Transcripciones
      </Typography>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar por nombre de archivo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Transcriptions Table */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell><strong>Archivo</strong></TableCell>
              <TableCell><strong>Duración</strong></TableCell>
              <TableCell><strong>Tamaño</strong></TableCell>
              <TableCell><strong>Fecha</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 1 }}>Cargando transcripciones...</Typography>
                </TableCell>
              </TableRow>
            ) : transcriptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No se encontraron transcripciones
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              transcriptions.map((transcription) => (
                <TableRow key={transcription.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {transcription.original_filename}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {transcription.id.slice(0, 8)}...
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDuration(transcription.duration_seconds)}</TableCell>
                  <TableCell>{transcription.file_size_mb} MB</TableCell>
                  <TableCell>{formatDate(transcription.created_at)}</TableCell>
                  <TableCell>
                    <Chip
                      label={transcription.status}
                      color={getStatusColor(transcription.status)}
                      size="small"
                    />
                    {transcription.has_diarization && (
                      <Chip
                        label={`${transcription.num_speakers} hablantes`}
                        color="primary"
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {transcription.diarization_status === 'processing' && (
                      <Chip
                        label="Separando..."
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => openDiarization(transcription)}
                      disabled={transcription.diarization_status === 'processing'}
                      title={transcription.has_diarization ? 'Re-separar hablantes' : 'Separar hablantes'}
                    >
                      {transcription.diarization_status === 'processing' ? (
                        <CircularProgress size={16} />
                      ) : (
                        <RecordVoiceOverIcon />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setSelectedTranscription(transcription)}
                      title="Ver detalles"
                    >
                      <PlayIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleExport(transcription.id, 'txt')}
                      disabled={exporting === 'txt'}
                      title="Exportar TXT"
                    >
                      {exporting === 'txt' ? <CircularProgress size={16} /> : <DownloadIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(transcription.id)}
                      color="error"
                      title="Eliminar"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Transcription Details Dialog */}
      <Dialog
        open={!!selectedTranscription}
        onClose={() => setSelectedTranscription(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          📝 Detalles de Transcripción
        </DialogTitle>
        <DialogContent>
          {selectedTranscription && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedTranscription.original_filename}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography><strong>ID:</strong> {selectedTranscription.id}</Typography>
                <Typography><strong>Modelo:</strong> {selectedTranscription.model_used}</Typography>
                <Typography><strong>Fecha:</strong> {formatDate(selectedTranscription.created_at)}</Typography>
              </Box>

              {/* Export Options */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>📥 Exportar</Typography>

                {/* Content Selection */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Seleccionar contenido:</Typography>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={exportOptions.includeTranscription}
                          onChange={(e) => setExportOptions(prev => ({ ...prev, includeTranscription: e.target.checked }))}
                        />
                      }
                      label="Transcripción"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={exportOptions.includeTimestamps}
                          onChange={(e) => setExportOptions(prev => ({ ...prev, includeTimestamps: e.target.checked }))}
                        />
                      }
                      label="Marcas de tiempo"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={exportOptions.includeSummary}
                          onChange={(e) => setExportOptions(prev => ({ ...prev, includeSummary: e.target.checked }))}
                        />
                      }
                      label="Resumen"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={exportOptions.includeSpeakers}
                          onChange={(e) => setExportOptions(prev => ({ ...prev, includeSpeakers: e.target.checked }))}
                        />
                      }
                      label="Hablantes"
                    />
                  </FormGroup>
                </Box>

                {/* Format Buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={exporting === 'txt' ? <CircularProgress size={16} /> : <DownloadIcon />}
                    onClick={() => handleExport(selectedTranscription.id, 'txt')}
                    disabled={exporting === 'txt'}
                  >
                    TXT
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={exporting === 'md' ? <CircularProgress size={16} /> : <DownloadIcon />}
                    onClick={() => handleExport(selectedTranscription.id, 'md')}
                    disabled={exporting === 'md'}
                  >
                    Markdown
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={exporting === 'pdf' ? <CircularProgress size={16} /> : <DownloadIcon />}
                    onClick={() => handleExport(selectedTranscription.id, 'pdf')}
                    disabled={exporting === 'pdf'}
                  >
                    PDF
                  </Button>
                </Box>
              </Box>

              {/* Transcription Content */}
              <TranscriptionViewer transcription={selectedTranscription} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTranscription(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!diarizationTarget} onClose={closeDiarization} maxWidth="xs" fullWidth>
        <DialogTitle>Separar hablantes</DialogTitle>
        <DialogContent>
          {diarizationTarget && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {diarizationTarget.original_filename}
            </Typography>
          )}

          {(diarizationStatus === 'idle' || diarizationStatus === 'failed') && (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Número de hablantes</InputLabel>
              <Select
                value={diarizationNumSpeakers}
                label="Número de hablantes"
                onChange={(e) => setDiarizationNumSpeakers(e.target.value)}
              >
                <MenuItem value="auto">Automático</MenuItem>
                <MenuItem value="2">2 hablantes</MenuItem>
                <MenuItem value="3">3 hablantes</MenuItem>
                <MenuItem value="4">4 hablantes</MenuItem>
                <MenuItem value="5">5 hablantes</MenuItem>
                <MenuItem value="6">6 hablantes</MenuItem>
              </Select>
            </FormControl>
          )}

          {diarizationStatus === 'processing' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">
                {diarizationMessage || 'Separando hablantes...'}
              </Typography>
            </Box>
          )}

          {diarizationStatus === 'completed' && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Hablantes separados y guardados correctamente.
            </Alert>
          )}

          {diarizationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {diarizationError}
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            La transcripción original no cambia; se añaden etiquetas de hablante y marcas de tiempo.
          </Typography>
        </DialogContent>
        <DialogActions>
          {diarizationStatus === 'idle' && (
            <>
              <Button onClick={closeDiarization}>Cancelar</Button>
              <Button onClick={startDiarization} variant="contained">Separar</Button>
            </>
          )}
          {diarizationStatus === 'failed' && (
            <Button onClick={() => { setDiarizationStatus('idle'); setDiarizationError(''); }}>
              Reintentar
            </Button>
          )}
          <Button onClick={closeDiarization}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const formatTimestamp = (seconds) => {
  const total = Math.max(0, Math.floor(seconds || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Separate component for viewing transcription content
const TranscriptionViewer = ({ transcription }) => {
  const [transcriptionData, setTranscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (transcription) {
      fetchTranscriptionData();
    }
  }, [transcription]);

  const fetchTranscriptionData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/transcription/${transcription.id}`);
      setTranscriptionData(response.data);
    } catch (error) {
      console.error('Error fetching transcription data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!transcriptionData) {
    return <Typography color="error">Error al cargar los datos de transcripción</Typography>;
  }

  return (
    <Box>
      {transcriptionData.transcription?.speakers?.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {transcriptionData.transcription.speakers.map((speaker) => (
            <Chip
              key={speaker.id}
              size="small"
              label={speaker.name}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      )}

      {/* Transcription */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>🎙️ Transcripción Completa</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
            {transcriptionData.transcription?.text || "No disponible"}
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Segments */}
      {transcriptionData.transcription?.segments && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>⏱️ Marcas de Tiempo</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {transcriptionData.transcription.segments.map((segment, i) => (
              <Typography key={i} variant="body2" sx={{ mb: 1 }}>
                <strong>[{formatTimestamp(segment.start)} - {formatTimestamp(segment.end)}]</strong>{' '}
                {segment.speaker ? `${segment.speaker}: ` : ''}{segment.text}
              </Typography>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Summary */}
      {transcriptionData.summary && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>📋 Resumen Inteligente</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{
              '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1, fontWeight: 600 },
              '& ul, & ol': { pl: 3 },
              '& li': { mb: 0.5 },
              '& p': { mb: 1 },
              '& strong': { fontWeight: 600 },
              '& hr': { my: 2, border: 'none', borderTop: '1px solid rgba(0,0,0,0.1)' },
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                marginBottom: 2,
                border: '1px solid rgba(0,0,0,0.1)'
              },
              '& th, & td': {
                border: '1px solid rgba(0,0,0,0.1)',
                padding: '8px 12px',
                textAlign: 'left'
              },
              '& th': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                fontWeight: 600
              },
              '& tr:nth-of-type(even)': {
                backgroundColor: 'rgba(0,0,0,0.02)'
              }
            }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{transcriptionData.summary}</ReactMarkdown>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default TranscriptionsHistory;